import { SidebarLayout } from "../components/SidebarLayout";
import { useState } from "react";
import "@fontsource/allura/latin-400.css";
import "../styles/bio-refresh.css";
import "../styles/discord-bio.css";
import "../styles/tiktok-bio.css";
import { BioMaker, type BioTemplateSelection } from "../components/BioMaker";
import { ToolNavigation } from "../components/ToolNavigation";
import { SiteFooter } from "../components/SiteFooter";
import { ENGLISH_TOOL } from "../data/englishTool";
import { TIKTOK_STEPS, TIKTOK_EXAMPLES, TIKTOK_GUIDE, TIKTOK_FAQ } from "../data/tiktokBioContent";
import { copyText } from "../lib/copyText";
import { trackAnalyticsEvent } from "../lib/analytics";

const labels = { ...ENGLISH_TOOL, fontHeading: "TikTok Bio Fonts", templateHeading: "TikTok Bio Templates", symbolsHeading: "TikTok Bio Emojis & Symbols", previewHeading: "TikTok Bio Preview", copy: "Copy TikTok Bio", copyAriaLabel: "Copy TikTok Bio" };

export function TikTokBioPage() {
  const [selection, setSelection] = useState<BioTemplateSelection>();
  const [copyStatus, setCopyStatus] = useState<{ id: string; message: string }>();
  const useBio = (text: string, group: string) => {
    setSelection(previous => ({ text, key: (previous?.key ?? 0) + 1 }));
    trackAnalyticsEvent("template_selected", { template_group: group, selection_source: "tiktok_gallery", page_language: "en" });
  };
  const copyBio = async (text: string, id: string) => {
    try {
      await copyText(text);
      setCopyStatus({ id, message: "Copied!" });
    } catch {
      setCopyStatus({ id, message: "Copy didn’t work. Select the bio text and copy it manually." });
    }
  };
  return <div className="site-shell bio-refresh discord-page tiktok-page">
    <header className="topbar">
      <a className="brand" href="/" aria-label="Aesthetic Bio Maker home">
        <span className="brand-mark"><img src="/favicon-96x96.png" alt="" width={38} height={38} /></span>
        <span>Aesthetic Bio Maker</span>
      </a>
      <ToolNavigation current="tiktok" />
    </header>
    <SidebarLayout><main className="page-grid" id="top"><div className="page-center">
      <div className="tool-hero"><h1 id="generator-heading">TikTok Bio Maker<span> - Create an Aesthetic TikTok Bio with Fonts and Symbols</span></h1></div>
      <BioMaker labels={labels} selection={selection} multiline controlHeadings="h3" cssActionLabels />
      <a className="tiktok-browse" href="#bio-ideas">Browse TikTok Bio Ideas ↓</a>
      <section className="content-section how-to-section" aria-labelledby="how-to">
        <div className="section-heading"><h2 id="how-to">How to Use the TikTok Bio Maker</h2></div>
        <ol className="how-to-list">{TIKTOK_STEPS.map(([title, description]) => <li key={title}><strong>{title}</strong><br />{description}</li>)}</ol>
      </section>
      <section className="content-section examples-section" id="bio-ideas" aria-labelledby="ideas-heading">
        <div className="section-heading"><h2 id="ideas-heading">TikTok Bio Ideas to Copy and Paste</h2><p>Find an aesthetic TikTok bio or a cute line that feels like you. Copy your favorite, or select Use this bio to personalize it with fonts and symbols.</p></div>
        {TIKTOK_EXAMPLES.map(group => <section className="tiktok-example-group" key={group.id} aria-labelledby={`${group.id}-heading`}>
          <div className="discord-style-heading"><h3 id={`${group.id}-heading`}>{group.title}</h3><p>{group.description}</p></div>
          <div className="discord-template-grid">{group.bios.map((text, index) => {
            const id = `${group.id}-${index}`;
            return <article className="discord-template-card" key={id}>
              <p className="tiktok-bio-text">{text}</p>
              <div className="tiktok-card-actions">
                <button type="button" onClick={() => copyBio(text, id)} aria-label={`Copy ${group.title} example ${index + 1}`}><span className="css-action-label" data-label="Copy" aria-hidden="true" /></button>
                <button type="button" onClick={() => useBio(text, group.id)} aria-label={`Use ${group.title} example ${index + 1}`}><span className="css-action-label" data-label="Use this bio ↗" aria-hidden="true" /></button>
              </div>
              <span className="tiktok-copy-status" role="status">{copyStatus?.id === id ? copyStatus.message : ""}</span>
            </article>;
          })}</div>
        </section>)}
      </section>
      <section className="content-section tiktok-guide" aria-labelledby="writing-heading">
        <div className="section-heading"><h2 id="writing-heading">How to Write a Good Bio for TikTok</h2><p>A good bio for TikTok gives someone a quick sense of who you are or what you share. Choose one detail you want people to remember, and use words you would say yourself.</p></div>
        {TIKTOK_GUIDE.map(([question, answer]) => <div key={question}><h3>{question}</h3><p>{answer}</p></div>)}
      </section>
      <section className="content-section faq-section discord-faq" aria-labelledby="faq-heading">
        <div className="section-heading"><h2 id="faq-heading">TikTok Bio FAQ</h2></div>
        <div className="faq-list">{TIKTOK_FAQ.map(([question, answer]) => <details key={question}><summary><h3>{question}</h3></summary><p>{answer}</p></details>)}</div>
      </section>
    </div></main></SidebarLayout>
    <SiteFooter appearance="brand" />
  </div>;
}
